/**
 * PasswordStrengthBar — bar de 6px cu gradient fill color-aware (rose→amber→
 * yellow→mint) + label + readout mare. Inspired codex-v6 „strength-demo".
 * Folosit împreună cu zxcvbn score (0-4).
 */
import { useTranslation } from 'react-i18next';

interface Props {
  /** score 0..4 (zxcvbn). */
  score: number;
  /** Etichetă text (ex. „foarte puternică", etc.). Lăsată goală = no label. */
  label?: string;
  /** Sub-label mono uppercase (ex. „AES-256-GCM"). */
  subLabel?: string;
  /** Compact: fără readout mare, doar bar. */
  compact?: boolean;
}

const PERCENT_PER_SCORE = [12, 32, 56, 78, 100];

// Chei i18n — traduse la randare, nu la încărcarea modulului.
const READOUT_LABEL_KEYS = [
  'vault_ui.strength.very_weak',
  'vault_ui.strength.weak',
  'vault_ui.strength.ok',
  'vault_ui.strength.good',
  'vault_ui.strength.very_good',
];

const READOUT_COLORS = [
  'var(--c-rose)',
  'var(--c-amber)',
  'var(--c-yellow)',
  'var(--c-mint)',
  'var(--c-mint)',
];

export default function PasswordStrengthBar({ score, label, subLabel, compact }: Props) {
  const { t } = useTranslation();
  const clamped = Math.max(0, Math.min(4, score));
  const percent = PERCENT_PER_SCORE[clamped];
  const readoutText = label ?? t(READOUT_LABEL_KEYS[clamped]);
  const readoutColor = READOUT_COLORS[clamped];

  if (compact) {
    return (
      <div
        className="relative overflow-hidden rounded-[3px]"
        style={{
          height: 6,
          background: 'var(--strength-empty)',
        }}
        aria-label={t('vault_ui.strength.aria', { label: readoutText })}
      >
        <div
          className="absolute inset-0 rounded-[3px] transition-[width]"
          style={{
            width: `${percent}%`,
            background: 'var(--grad-strength)',
            boxShadow: '0 0 12px rgba(19, 233, 180, 0.3)',
          }}
        />
      </div>
    );
  }

  return (
    <div
      className="rounded-xl p-4 grid items-center gap-5"
      style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border)',
        gridTemplateColumns: '1fr auto',
      }}
    >
      <div>
        <div
          className="font-mono uppercase mb-2.5"
          style={{
            fontSize: 10,
            letterSpacing: '0.14em',
            color: 'var(--text-muted)',
          }}
        >
          {t('vault_ui.strength.title')}
        </div>
        <div
          className="relative overflow-hidden rounded-[3px]"
          style={{
            height: 6,
            background: 'var(--strength-empty)',
          }}
        >
          <div
            className="absolute inset-0 rounded-[3px] transition-[width]"
            style={{
              width: `${percent}%`,
              background: 'var(--grad-strength)',
              boxShadow: '0 0 12px rgba(19, 233, 180, 0.3)',
            }}
          />
        </div>
      </div>
      <div className="text-right">
        <div
          className="font-mono"
          style={{
            fontSize: 22,
            fontWeight: 600,
            letterSpacing: '-0.01em',
            color: readoutColor,
          }}
        >
          {readoutText}
        </div>
        {subLabel && (
          <div
            className="font-mono uppercase mt-1"
            style={{
              fontSize: 10,
              letterSpacing: '0.1em',
              color: 'var(--text-dim)',
            }}
          >
            {subLabel}
          </div>
        )}
      </div>
    </div>
  );
}
